import { useCallback, useEffect, useRef, useState } from "react";
import { useAppRuntime } from "../../../providers/AppRuntimeProvider";
import type { ContactGroup } from "../types";
import type { PaginatedResponse } from "../../../hooks/types";

interface UseGroupsResult {
  readonly groups: readonly ContactGroup[];
  readonly loading: boolean;
  readonly createGroup: (name: string, color?: string) => Promise<ContactGroup>;
  readonly updateGroup: (
    id: string,
    updates: {
      name?: string;
      color?: string;
      description?: string;
      memory?: string;
    },
  ) => Promise<void>;
  readonly deleteGroup: (id: string) => Promise<void>;
  readonly reload: () => Promise<void>;
}

export function useGroups(): UseGroupsResult {
  const runtime = useAppRuntime();
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const result =
        await runtime.transport.rpc<PaginatedResponse<ContactGroup>>("groups.list", { limit: 1000 });
      setGroups([...result.items]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [load]);

  const createGroup = useCallback(
    async (name: string, color?: string): Promise<ContactGroup> => {
      const group = await runtime.transport.rpc<ContactGroup>("groups.create", {
        name,
        color: color ?? "#FF8400",
      });
      setGroups((prev) => [...prev, group]);
      return group;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const updateGroup = useCallback(
    async (
      id: string,
      updates: {
        name?: string;
        color?: string;
        description?: string;
        memory?: string;
      },
    ): Promise<void> => {
      const updated = await runtime.transport.rpc<ContactGroup>(
        "groups.update",
        { id, ...updates },
      );
      setGroups((prev) => prev.map((g) => (g.id === id ? updated : g)));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const deleteGroup = useCallback(async (id: string): Promise<void> => {
    await runtime.transport.rpc("groups.delete", { id });
    setGroups((prev) => prev.filter((g) => g.id !== id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { groups, loading, createGroup, updateGroup, deleteGroup, reload: load };
}
